<template>
  <b-modal
    id="application-details-modal"
    ref="application-details-modal"
    size="lg"
    header-class="custom-modal"
    body-class="custom-modal"
    footer-class="custom-modal"
    :hide-footer="true"
    @hidden="onModalHidden"
    @shown="onModalShown"
  >
    <template #modal-title>
      <b-container>
        <b-row align-h="end">
          <b-col>
            Application Details: {{ registration.sellerOffer.name }} 
          </b-col>
          <b-col cols="2">
            <b-badge 
              :variant="getBadgeVariant(registration.status)"
              pill 
              right-align
            >
              {{ registration.status }}
            </b-badge>
          </b-col>
        </b-row>
      </b-container>
    </template>

    <div class="px-4">
      <div
        v-if="showInsights && loadingInsights"
        class="mb-3"
      >
        <Loader />
      </div>
      <div
        v-else-if="showInsights && offerInsights"
        class="mb-3"
      >
        <InsightsDisplay :insights="offerInsights" />
        <hr>
      </div>

      <BaseFormInput
        id="seller-offer__created-at"
        v-model="createdAt"
        :horizontal="true"
        :small="true"
        internal-platform-input
        label="Applied At"
        type="text"
        width-lg="4"
        width-xl="3"
        has-tooltip
        plaintext
        input-class="px-0"
      />
      <hr>

      <BaseFormTextArea
        v-if="registration.reason"
        v-model="registration.reason"
        :horizontal="true"
        :small="true"
        internal-platform-input
        label="Rejection reason"
        type="text"
        width-lg="4"
        width-xl="3"
        rows="5"
        plaintext
        disabled
        input-class="px-0"
        placeholder="The reason for the rejection"
      />

      
      <BaseFormTextArea
        id="seller-offer__description"
        v-model="registration.sellerOffer.description"
        :horizontal="true"
        :small="true"
        internal-platform-input
        label="Description"
        type="text"
        width-lg="4"
        width-xl="3"
        rows="5"
        disabled
        has-tooltip
        plaintext
        input-class="px-0"
      />
      <BaseFormSelect
        id="seller-offer__category"
        v-model="categoryId"
        :horizontal="true"
        internal-platform-input
        :small="true"
        name="Category"
        label="Category"
        text-field="name"
        value-field="id"
        :options="categoryList"
        required
        :disabled="true"
        width="5"
        width-lg="4"
        width-xl="3"
        has-tooltip
      />
      <BaseFormSelect
        id="seller-offer__traffic-source-type"
        v-model="allowedTrafficSourceType"
        :horizontal="true"
        internal-platform-input
        :small="true"
        name="Traffic Source Type"
        label="Traffic Source Type"
        :options="allowedTrafficSourceTypeList"
        required
        :disabled="true"
        width="5"
        width-lg="4"
        width-xl="3"
        has-tooltip
      />



      <div class="mt-4">
        Traffic Sources
        <span
          v-b-tooltip.hover.right
          :title="'The types of promotional methods allowed for this offer'"
        >
          <font-awesome-icon
            class="ml-1 mt-1"
            :icon="['fas', 'question-circle']"
          />
        </span>
      </div>
      <b-row>
        <b-col 
          lg="6"
          class="my-1"
        >
          <b-table 
            striped 
            hover 
            :items="allowedTrafficSources" 
            :fields="allowedTrafficSourcesFields"
          >
            <template #cell(name)="row">
              <div class="text-nowrap">
                {{ row.item.name }}
              </div>
            </template>
          </b-table>
        </b-col>
        <b-col 
          lg="6" 
          class="my-1"
        >
          <b-table 
            striped 
            hover 
            :items="selectedTrafficSources" 
            :fields="[{ key: 'name', label: 'Selected Traffic Sources' }]"
          >
            <template #cell(name)="row">
              <div class="text-nowrap">
                {{ row.item.name }}
              </div>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <BaseButtonGroup
        internal-platform-input
        :selected-option="registration.thirdPartyTraffic"
        label="Will any of your traffic come from third party affiliates?"
        :options="['Yes', 'No', 'Some']"
      />

      <BaseButtonGroup
        internal-platform-input
        :selected-option="registration.isAffiliateTrafficApproved"
        label="Was affiliate traffic allowed?"
        :options="[
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ]"
      />

      <BaseFormTextArea
        id="landingPageURLs"
        v-model="registration.landingPageUrls"
        internal-platform-input
        :small="true"
        rows="4"
        data-cy="input-landing-page-urls"
        name="input-landing-page-urls"
        label="Please provide URLs for landing pages if applicable:"
        placeholder="None"
        plaintext
        disabled
      />

      <BaseFormTextArea
        id="promotionalMethodsDetails"
        v-model="registration.promotionalMethodsDetails"
        internal-platform-input
        :small="true"
        rows="4"
        label="Please provide specific details regarding your promotional methods:"
        placeholder="None"
        plaintext
        disabled
      />

      <div class="mt-4">
        <div class="mb-3">
          Creative Files
          <span
            v-b-tooltip.hover.right
            :title="'Modify previously uploaded creatives by adding or removing files. Be warned your offer application will be paused until admins review updated creative files.'"
          >
            <font-awesome-icon
              class="ml-1 mt-1"
              :icon="['fas', 'question-circle']"
            />
          </span>
        </div>

        <div>
          <div
            v-if="isMedicareCategory"
            class="text-muted"
          >
            Medicare creatives must be uploaded separately
          </div>

          <b-button
            v-else-if="showAddCreativeButton"
            type="button"
            size="sm"
            @click="addCreativeInput"
          >
            Add creative file
          </b-button>

          <div v-if="!showAddCreativeButton && !isMedicareCategory">
            <div
              v-for="(creative, idx) in creatives"
              :key="idx"
              class="mb-3"
            >
              <div class="form-row align-items-start">
                <div class="col-12 col-sm-4">
                  <b-form-select
                    :id="`creative-traffic-source-${idx}`"
                    v-model="creative.trafficSourceId"
                    class="form-control custom-form-input--xs"
                    size="sm"
                    :options="trafficSourceOptions"
                    :disabled="creative.uploaded"
                    @input="updateCreativeTrafficSource(idx, $event)"
                  >
                    <template #first>
                      <b-form-select-option
                        :value="''"
                        disabled
                      >
                        Select traffic source
                      </b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-12 col-sm-4 mt-2 mt-sm-0">
                  <b-form-file
                    v-if="!creative.isAdditionalFile"
                    v-model="creative.file"
                    accept=".jpg,.png,.jpeg,.mp4,.mov,.pdf,.avi,.mp3,.wav"
                    size="sm"
                    placeholder="Choose file(s)"
                    multiple
                    :disabled="!creative.trafficSourceId"
                    @input="handleCreativeFileSelected(idx)"
                  />
                  <div
                    v-else
                    class="form-control form-control-sm"
                    style="background-color: #e9ecef; cursor: not-allowed; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                    :title="creative.fileName"
                  >
                    {{ creative.fileName }}
                  </div>
                </div>

                <div class="col-auto">
                  <b-button
                    type="button"
                    class="ml-2 bg-transparent border-0"
                    :disabled="creative.progress > 0 && creative.progress < 100 && !creative.error"
                    @click="removeCreativeInput(idx)"
                  >
                    <font-awesome-icon :icon="['fas', 'minus-circle']" />
                  </b-button>
                  <b-button
                    v-if="idx === creatives.length - 1"
                    type="button"
                    class="ml-2 bg-transparent border-0"
                    @click="addCreativeInput"
                  >
                    <font-awesome-icon :icon="['fas', 'plus-circle']" />
                  </b-button>
                  <b-button
                    v-if="creative.error"
                    type="button"
                    class="ml-2 bg-transparent border-0"
                    @click="retryCreativeUpload(idx)"
                  >
                    <font-awesome-icon :icon="['fas', 'sync']" />
                  </b-button>
                </div>
              </div>

              <div
                v-if="creative.progress > 0 && creative.progress < 100 && !creative.uploaded"
                class="form-row mt-2"
              >
                <div class="col-12 col-sm-8">
                  <b-progress
                    :value="creative.progress"
                    :max="100"
                    animated
                  />
                  <small>File size {{ formatFileSize(creative.file.size) }}</small>
                  <div v-if="creative.error">
                    <small class="text-danger">
                      <p>
                        Upload failed <br>
                        {{ creative.error }}
                      </p>
                    </small>
                  </div>
                </div>
              </div>

              <div
                v-if="creative.uploaded"
                class="form-row mt-1"
              >
                <div class="col-12">
                  <small class="text-success">
                    <font-awesome-icon :icon="['fas', 'check']" />
                    {{ creative.fileName }} uploaded successfully
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <BaseButtonLoader
          v-if="hasCreativeChanges"
          class="mt-4"
          internal-platform-input
          :disabled="!canSaveCreatives"
          variant="primary"
          :loading="savingCreatives"
          @click="showSaveCreativesConfirmation"
        >
          Save modified creatives
        </BaseButtonLoader>
      </div>
    </div>

    <b-modal
      id="save-creatives-confirmation-modal"
      ref="save-creatives-confirmation-modal"
      title="Confirm Creative Files Update"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      ok-title="Update"
      @ok="saveCreatives"
    >      
      <p>Are you sure you want to update your creative files?</p>
    </b-modal>
  </b-modal>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { formatFileSize } from '@/utils/formatFileSize'
import InsightsDisplay from './InsightsDisplay.vue'

export default {
  name: 'SellerApplicationDetailsModal',

  components: {
    InsightsDisplay
  },

  props: {
    registration: {
      type: Object,
      default: () => {
        return {
          status: null,
          sellerOffer: {
            id: null,
            name: null,
            description: null,
            status: null,
            createdAt: null,
            createdBy: null,
            category: {
              id: null,
              name: null,
            },
            allowedTrafficSourceType: null,
          },
        }
      },
    },
    showInsights: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      creatives: [],
      originalCreativeFileIds: [],
      savingCreatives: false,
      loadingInsights: false,
    }
  },
  computed:{
    ...mapGetters('insights', [
      'getInsightsByOfferId'
    ]),

    offerInsights() {
      const sellerOfferId = this.registration.sellerOffer?.id
      return this.getInsightsByOfferId(sellerOfferId)
    },

    categoryList() {
      const categoryList = [this.registration.sellerOffer.category]
      return categoryList
    },

    categoryId: {
      get() {
        return this.registration.sellerOffer.category?.id
      }
    },

    allowedTrafficSourceTypeList() {
      const categoryList = [this.registration.sellerOffer.allowedTrafficSourceType]
      return categoryList
    },

    allowedTrafficSourceType: {
      get() {
        return this.registration.sellerOffer.allowedTrafficSourceType
      }
    },
    
    allowedTrafficSourcesFields: {
      get() {
        if (this.registration.approvedTrafficSources?.length > 0) {
          return [{ key: 'name', label: 'Approved Traffic Sources' }]
        }
        return [
          { key: 'name', label: 'Allowed Traffic Sources' }
        ]
      }
    },

    allowedTrafficSources: {
      get() {
        if (this.registration.approvedTrafficSources?.length > 0) {
          return this.registration.approvedTrafficSources
        }
        return this.registration.sellerOffer.allowedTrafficSources
      }
    },

    selectedTrafficSources: {
      get() {
        return this.registration.selectedTrafficSources || []
      }
    },
    createdAt() {
      return moment(this.registration.createdAt).format('MMM DD, YYYY')
    },

    creativeFilesFields() {
      return [
        { key: 'fileName', label: 'File Name' },
        { key: 'trafficSourceName', label: 'Traffic Source' }
      ]
    },

    showAddCreativeButton() {
      return !this.creatives.length
    },

    trafficSourceOptions() {
      const selectedIds = this.registration.selectedTrafficSources?.map(ts => ts.id) || []

      return this.registration.sellerOffer.allowedTrafficSources?.map(ts => ({
        value: ts.id,
        text: ts.name,
        disabled: !selectedIds.includes(ts.id)
      })) || []
    },

    hasCreativeChanges() {
      if (this.creatives.length === 0 && this.originalCreativeFileIds.length === 0) {
        return false
      }

      const currentFileIds = this.creatives
        .filter(c => c.uploaded && c.fileId)
        .map(c => c.fileId)
        .sort()

      const originalIds = [...this.originalCreativeFileIds].sort()

      if (currentFileIds.length !== originalIds.length) {
        return true
      }

      return currentFileIds.some((id, idx) => id !== originalIds[idx])
    },

    canSaveCreatives() {
      return this.creatives.length > 0 &&
             this.creatives.every(c => c.uploaded && !c.error && c.fileId)
    },

    isMedicareCategory() {
      return this.registration.sellerOffer?.category?.isMedicare || false
    },

  },
  watch: {
    registration: {
      handler(newVal) {
        if (newVal && newVal.creativeFiles) {
          this.initializeCreatives()
        }
      },
      immediate: true,
      deep: true,
    },
  },
  destroyed() {
    this.RESET_CREATIVE_FILES()
  },
  methods:{
    ...mapActions('creative', ['uploadFile']),
    ...mapActions('sellerOffer', ['updateCreativeFiles', 'getSellerAppliedOffers']),
    ...mapActions('insights', ['getSellerOfferInsights']),
    ...mapMutations('creative', ['RESET_CREATIVE_FILES', 'REMOVE_CREATIVE_FILE']),

    formatFileSize,

    onModalHidden() {
      this.$emit('hidden')
    },

    async onModalShown() {
      this.$emit('shown')
      await this.fetchInsights()
    },

    async fetchInsights() {
      if (!this.showInsights) {
        return
      }

      const sellerOfferId = this.registration.sellerOffer?.id
      if (!sellerOfferId) {
        return
      }

      // First check if insights are in cache
      const cachedInsights = this.getInsightsByOfferId(sellerOfferId)
      if (cachedInsights) {
        return
      }

      // If not in cache, fetch individually
      try {
        this.loadingInsights = true
        await this.getSellerOfferInsights(sellerOfferId)
      } catch {
        // Silently handle errors - insights section will not show
      } finally {
        this.loadingInsights = false
      }
    },

    initializeCreatives() {
      if (this.registration.creativeFiles && this.registration.creativeFiles.length > 0) {
        this.creatives = this.registration.creativeFiles.map(cf => ({
          fileId: cf.fileId,
          fileName: cf.fileName,
          file: null,
          uploaded: true,
          progress: 0,
          error: '',
          trafficSourceId: cf.trafficSourceId || '',
          isAdditionalFile: true
        }))
        this.originalCreativeFileIds = this.registration.creativeFiles.map(cf => cf.fileId)
      } else {
        this.creatives = []
        this.originalCreativeFileIds = []
      }
    },

    updateCreativeTrafficSource(index, trafficSourceId) {
      if (this.creatives[index]) {
        this.creatives[index].trafficSourceId = trafficSourceId
      }
    },

    addCreativeInput() {
      const lastCreative = this.creatives.length > 0 ? this.creatives[this.creatives.length - 1] : null
      const preSelectedTrafficSourceId = lastCreative?.trafficSourceId || ''

      this.creatives.push({
        fileId: '',
        fileName: '',
        file: null,
        uploaded: false,
        progress: 0,
        error: '',
        trafficSourceId: preSelectedTrafficSourceId
      })
    },

    removeCreativeInput(idx) {
      const removedCreative = this.creatives[idx]

      if (removedCreative.uploaded && removedCreative.fileId) {
        this.REMOVE_CREATIVE_FILE(removedCreative.fileId)
      }

      this.creatives.splice(idx, 1)
    },

    async uploadNewCreative(event) {
      if (!event || !event.list) {
        return
      }

      const creative = this.creatives[event.index]

      if (!creative.trafficSourceId) {
        this.creatives[event.index].error = 'Please select a traffic source'
        showErrorMessage('Please select a traffic source for the creative')
        return
      }

      try {
        const responseData = await this.uploadFile({
          request: {
            file: event.list[event.index].file,
            index: event.index,
            isApprovalDoc: false
          },
          progressCallback: this.creativeUploadProgress
        })

        this.creatives[event.index].fileId = responseData
        this.creatives[event.index].uploaded = true
        this.creatives[event.index].progress = 0
        this.creatives[event.index].error = ''

        showSuccessMessage('Successfully uploaded ' + this.creatives[event.index].fileName)
      } catch (error) {
        this.creatives[event.index].error = error
        showErrorMessage(error)
      }
    },

    creativeUploadProgress(value) {
      if (this.creatives[value.index]) {
        this.creatives[value.index].progress = parseInt(Math.round(value.progress))
      }
    },

    handleCreativeFileSelected(index) {
      const creative = this.creatives[index]

      if (creative.uploaded && creative.fileId) {
        this.REMOVE_CREATIVE_FILE(creative.fileId)
      }

      if (creative.file != null) {
        const files = Array.isArray(creative.file) ? creative.file : [creative.file]

        if (files.length === 0) {
          return
        }

        const trafficSourceId = creative.trafficSourceId

        creative.file = files[0]
        creative.fileName = files[0].name
        creative.progress = 0
        creative.uploaded = false

        this.uploadNewCreative({
          list: this.creatives,
          index: index
        })

        for (let i = 1; i < files.length; i++) {
          const newCreative = {
            fileId: '',
            fileName: files[i].name,
            file: files[i],
            uploaded: false,
            progress: 0,
            error: '',
            trafficSourceId: trafficSourceId,
            isAdditionalFile: true
          }

          this.creatives.push(newCreative)

          const newIndex = this.creatives.length - 1
          this.uploadNewCreative({
            list: this.creatives,
            index: newIndex
          })
        }
      }
    },

    retryCreativeUpload(index) {
      this.creatives[index].error = ''
      this.creatives[index].progress = 0
      this.creatives[index].uploaded = false

      this.uploadNewCreative({
        list: this.creatives,
        index: index
      })
    },

    showSaveCreativesConfirmation() {
      this.$bvModal.show('save-creatives-confirmation-modal')
    },

    async saveCreatives() {
      try {
        this.savingCreatives = true

        const creativeFiles = this.creatives.map(creative => {
          const trafficSource = this.registration.sellerOffer.allowedTrafficSources.find(
            ts => ts.id === creative.trafficSourceId
          )
          return {
            fileId: creative.fileId,
            trafficSourceId: creative.trafficSourceId,
            trafficSourceName: trafficSource?.name || '',
            fileName: creative.fileName
          }
        })

        await this.updateCreativeFiles({
          registrationId: this.registration.id,
          creativeFiles: creativeFiles
        })

        this.originalCreativeFileIds = creativeFiles.map(cf => cf.fileId)

        await this.getSellerAppliedOffers()

        showSuccessMessage('Successfully updated creative files. Your application will be reviewed by an admin.')
        this.$bvModal.hide('application-details-modal')
      } catch (error) {
        showErrorMessage(`Failed to update creative files: ${error.response?.data?.title || error.message}`)
      } finally {
        this.savingCreatives = false
      }
    },
    getBadgeVariant(status) {
      const statusToVariantMap = {
        Pending: 'secondary',
        Approved: 'success',
        Rejected: 'danger',
        NotEligible: 'danger',
        Withdrawn: 'warning',
        ManuallyBlocked: 'dark'
      }
      return statusToVariantMap[status]
    },
  }
}
</script>

<style lang="scss">
</style>
